"use client";

import { useEffect, useLayoutEffect } from "react";
import {
  PAINT_BOOT_CLAIM,
  PAINT_BOOT_DISMISS,
  readPaintBootState,
} from "@/components/studio-paint-boot-control";
import { BOOT_FRAME_ID } from "@/mos-app/studio-boot-frame-script";

/** Nobody claimed the frame (route without AuthGate, crashed chunk): let go. */
const UNCLAIMED_TIMEOUT_MS = 12_000;

/**
 * Hard ceiling, claimed or not. The skeleton now looks like the real chat, so
 * a frame left up by a stalled auth / user / shell-chunk step (slow phone
 * network, a dev recompile, a chunk that never resolves) reads as a frozen,
 * broken app. After this long the frame always lets go; whatever AuthGate has
 * underneath (shell, its own pane skeletons, sign-in) takes over.
 */
const HARD_TIMEOUT_MS = 15_000;

/** Fade (180ms, studio-boot-frame.css) plus slack, then display:none. */
const BOOT_FRAME_GONE_MS = 260;

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Re-run the parser-blocking head scripts if React's dev remount wiped the
 * attributes they set on <html> (Strict Mode resets <html> to the attributes
 * it owns). A no-op in production, where hydration keeps them.
 */
function reapplyHeadBootIfCleared() {
  const root = document.documentElement;
  if (root.dataset.appearance) return;
  for (const id of ["mos-theme-boot", "ys-boot-decision"]) {
    const script = document.getElementById(id);
    const code = script?.textContent;
    if (!code) continue;
    try {
      new Function(code)();
    } catch {
      /* the inline copy already ran once at parse time */
    }
  }
}

/**
 * Hands the static boot frame over to the real UI.
 *
 * The frame is server HTML switched on by an inline script (never React
 * state), so hydration cannot flash it. This only listens: when AuthGate
 * dismisses the boot (shell mounted, or sign-in needed) it waits two frames so
 * the shell's first paint — restored tabs included — is on screen underneath,
 * then fades the frame out. After that the frame is done for this page load;
 * a later claim (account switch, token refresh) never brings it back.
 */
export function StudioBootFrameController() {
  useIsoLayoutEffect(() => {
    reapplyHeadBootIfCleared();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    // Read the head script's decision from window, not <html>: see
    // reapplyHeadBootIfCleared for why <html> attributes are not reliable.
    const mode = (window as Window & { __ysBoot?: string }).__ysBoot;
    if (mode !== "app" && mode !== "auth") return;
    // "auth" has no frame: only the sign-in plate colour on <html>, dropped on
    // the same dismiss once sign-in (or the shell) has painted over it.
    const frame = document.getElementById(BOOT_FRAME_ID);
    const frameOn = Boolean(frame?.hasAttribute("data-on") && !frame.hasAttribute("data-off"));
    // Dev Strict Mode remount after the handover: still finish the removal.
    if (frame?.hasAttribute("data-off") && !frame.hasAttribute("data-gone")) {
      window.setTimeout(() => frame.setAttribute("data-gone", ""), BOOT_FRAME_GONE_MS);
    }

    let done = false;
    let raf1 = 0;
    let raf2 = 0;
    let unclaimedTimer = 0;
    let hardTimer = 0;

    const release = () => {
      if (done) return;
      done = true;
      window.clearTimeout(unclaimedTimer);
      window.clearTimeout(hardTimer);
      raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(() => {
          if (frameOn) frame?.setAttribute("data-off", "");
          root.removeAttribute("data-ys-boot");
          // After the 180ms fade, take the frame out of rendering entirely
          // (data-gone = display:none). Hidden but laid out, its infinite
          // animations (theme cycle, ring spinner, skeleton shimmer, empty-art
          // glow) kept restyling and repainting every frame for the whole
          // session. Not cleared on cleanup: the frame never comes back.
          if (frameOn) window.setTimeout(() => frame?.setAttribute("data-gone", ""), BOOT_FRAME_GONE_MS);
        });
      });
    };

    const onClaim = () => {
      window.clearTimeout(unclaimedTimer);
    };

    window.addEventListener(PAINT_BOOT_DISMISS, release);
    window.addEventListener(PAINT_BOOT_CLAIM, onClaim);

    // Page restored from the back/forward cache: the shell is already live.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) release();
    };
    window.addEventListener("pageshow", onPageShow);

    hardTimer = window.setTimeout(() => {
      if (!done && readPaintBootState() !== "dismissed") {
        console.warn("[boot] frame still up after", HARD_TIMEOUT_MS, "ms; releasing", readPaintBootState());
      }
      release();
    }, HARD_TIMEOUT_MS);

    const state = readPaintBootState();
    if (state === "dismissed") {
      release();
    } else if (state !== "claimed") {
      unclaimedTimer = window.setTimeout(() => {
        if (readPaintBootState() === null) release();
      }, UNCLAIMED_TIMEOUT_MS);
    }

    return () => {
      window.removeEventListener(PAINT_BOOT_DISMISS, release);
      window.removeEventListener(PAINT_BOOT_CLAIM, onClaim);
      window.removeEventListener("pageshow", onPageShow);
      window.clearTimeout(unclaimedTimer);
      window.clearTimeout(hardTimer);
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, []);

  return null;
}
